import { SdkContext } from '../internal'
import type {
  DevelopmentActionInput,
  DevelopmentActionStatus,
  DevelopmentCurrentCycle,
  DevelopmentCycle,
  DevelopmentCycleAdhesion,
  DevelopmentCycleInput,
  DevelopmentCycleStatus,
  DevelopmentPlan,
  DevelopmentPlanCreateInput,
  DevelopmentPlanDashboard,
  DevelopmentPlanPage,
  DevelopmentPlanScope,
  DevelopmentPlanStatus,
  DevelopmentTeam
} from '@/types/developmentPlan'

const toPage = (result: unknown): DevelopmentPlanPage => {
  if (Array.isArray(result)) return { items: result as DevelopmentPlan[], pagination: null }
  const r = result as { items?: DevelopmentPlan[]; data?: DevelopmentPlan[]; pagination?: DevelopmentPlanPage['pagination'] } | null
  return { items: r?.items || r?.data || [], pagination: r?.pagination ?? null }
}

/**
 * F-52 PDI. The community comes from X-CommunityId (never the path); the
 * space from `spaceId`. Who reads or acts on a plan is the server's, per plan
 * (`can*`); anyone else gets 404.
 */
export class DevelopmentPlansResource {
  constructor(private ctx: SdkContext) {}

  async list(params: { scope?: DevelopmentPlanScope; spaceId?: string; status?: DevelopmentPlanStatus; memberId?: string; page?: number; limit?: number } = {}): Promise<DevelopmentPlanPage> {
    return toPage(await this.ctx.call({ httpMethod: 'GET', path: '/development-plans' }, { query: params }))
  }

  async team(spaceId?: string): Promise<DevelopmentTeam> {
    return this.ctx.call({ httpMethod: 'GET', path: '/development-plans/team' }, { query: { spaceId } })
  }

  /** Owner, admin, finance (the space) or a leader (their subtree); anyone else: 403. */
  async dashboard(spaceId?: string): Promise<DevelopmentPlanDashboard> {
    return this.ctx.call({ httpMethod: 'GET', path: '/development-plans/dashboard' }, { query: { spaceId } })
  }

  async get(id: string): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'GET', path: '/development-plans/{id}' }, { id })
  }

  async create(body: DevelopmentPlanCreateInput): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans', hasBody: true }, { body })
  }

  async update(id: string, body: { title?: string; startsOn?: string; endsOn?: string }): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/development-plans/{id}', hasBody: true }, { id, body })
  }

  /** Draft only (soft delete). */
  async remove(id: string): Promise<{ id: string; deleted: boolean }> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/development-plans/{id}' }, { id })
  }

  async submit(id: string): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/{id}/submit' }, { id })
  }

  async approve(id: string): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/{id}/approve' }, { id })
  }

  /** The note is required (422 `noteRequired`). */
  async returnPlan(id: string, note: string): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/{id}/return', hasBody: true }, { id, body: { note } })
  }

  async complete(id: string): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/{id}/complete' }, { id })
  }

  async addAction(id: string, body: DevelopmentActionInput): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/{id}/actions', hasBody: true }, { id, body })
  }

  /** `status` is progress and never asks for a new approval; the other fields are the plan. */
  async updateAction(id: string, actionId: string, body: Partial<DevelopmentActionInput> & { status?: DevelopmentActionStatus }): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/development-plans/{id}/actions/{actionId}', hasBody: true }, { id, actionId, body })
  }

  async removeAction(id: string, actionId: string): Promise<DevelopmentPlan> {
    return this.ctx.call({ httpMethod: 'DELETE', path: '/development-plans/{id}/actions/{actionId}' }, { id, actionId })
  }

  /* ---------- F-106 9: company cycles ---------- */

  /** The open cycle for the top of the space: its audience and owner/admin/finance; `null` otherwise. */
  async currentCycle(spaceId?: string): Promise<DevelopmentCurrentCycle | null> {
    const result = await this.ctx.call<DevelopmentCurrentCycle | null>({ httpMethod: 'GET', path: '/development-plans/cycles/current' }, { query: { spaceId } })
    return result && typeof result === 'object' && 'id' in result ? result : null
  }

  /** Owner, admin and finance (403 otherwise): the 50 newest cycles of the space. */
  async listCycles(params: { spaceId?: string; status?: DevelopmentCycleStatus } = {}): Promise<DevelopmentCycle[]> {
    const result = await this.ctx.call<unknown>({ httpMethod: 'GET', path: '/development-plans/cycles' }, { query: params })
    return Array.isArray(result) ? (result as DevelopmentCycle[]) : ((result as { items?: DevelopmentCycle[] } | null)?.items || [])
  }

  /** Owner and admin; 409 `cycleOpenExists` while another is open. */
  async createCycle(body: DevelopmentCycleInput): Promise<DevelopmentCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/cycles', hasBody: true }, { body })
  }

  async updateCycle(cycleId: string, body: DevelopmentCycleInput): Promise<DevelopmentCycle> {
    return this.ctx.call({ httpMethod: 'PUT', path: '/development-plans/cycles/{cycleId}', hasBody: true }, { cycleId, body })
  }

  /** Ends the reminders; the plans stay, linked to the cycle. */
  async closeCycle(cycleId: string): Promise<DevelopmentCycle> {
    return this.ctx.call({ httpMethod: 'POST', path: '/development-plans/cycles/{cycleId}/close', hasBody: true }, { cycleId, body: {} })
  }

  /** Owner, admin, finance (the whole audience) or a leader (their people); 403 otherwise. */
  async cycleAdhesion(cycleId: string): Promise<DevelopmentCycleAdhesion> {
    return this.ctx.call({ httpMethod: 'GET', path: '/development-plans/cycles/{cycleId}/adhesion' }, { cycleId })
  }
}
